import React from "react";
import {
  IconActivity,
  IconArrowsExchange,
  IconBrain,
  IconHeartbeat,
  IconLink,
  IconRefresh,
  IconStack2,
} from "@tabler/icons-react";
import {
  getSeverityClasses,
  comorbidityRiskTone,
  parseContributionPercent,
  complexityBadgeClass,
} from "./severity";
import type {
  BehavioralHealthAmplifier,
  ComorbidityAnalysisPanelProps,
  HighRiskDyad,
  HighRiskTriad,
  HighestComplexityCombination,
} from "./types";

const AiMark: React.FC = () => (
  <img
    src="/ai-bulwark-logo.svg"
    alt=""
    aria-hidden
    className="h-3.5 w-auto shrink-0 object-contain"
  />
);

const SectionRule: React.FC<{ color?: string }> = ({
  color = "#d1d5db",
}) => (
  <div className="ml-2 h-px min-w-0 flex-1" style={{ backgroundColor: color }} />
);

const ComplexityHeader: React.FC<{
  summary: string;
  overallComplexityLevel: string;
}> = ({ summary, overallComplexityLevel }) => {
  const tone = getSeverityClasses(overallComplexityLevel);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center gap-1.5">
            <h2 className="text-sm font-bold uppercase tracking-wider text-violet-700">
              Comorbidity Complexity
            </h2>
            <AiMark />
          </div>
          <p className="text-base leading-relaxed text-gray-700">{summary}</p>
        </div>
        <div
          className={`w-[160px] shrink-0 rounded-2xl border px-3 py-3 text-center ${tone.bg} ${tone.border}`}
        >
          <p className="whitespace-nowrap text-xs font-medium uppercase tracking-wider text-slate-500">
            Overall Complexity
          </p>
          <p
            className={`mt-1.5 text-xl font-bold uppercase leading-none ${tone.text}`}
          >
            {overallComplexityLevel}
          </p>
        </div>
      </div>
    </div>
  );
};

const HighestCombinationCard: React.FC<{
  combination: HighestComplexityCombination;
}> = ({ combination }) => {
  const tone = comorbidityRiskTone(combination.complexityScore);
  const score = Math.min(100, Math.max(0, combination.complexityScore));
  const isTriad = combination.complexityType.toLowerCase().includes("triad");
  const diagnoses = combination.diagnoses ?? [];
  const diagnosisCount = diagnoses.length;

  const rows = diagnoses.map((diagnosis) => ({
    diagnosis: diagnosis.diagnosis,
    contribution: diagnosis.contribution,
    percent: parseContributionPercent(diagnosis.contribution),
  }));

  const sorted =
    rows.every((row) => row.percent !== null)
      ? [...rows].sort((a, b) => (b.percent ?? 0) - (a.percent ?? 0))
      : rows;

  return (
    <section>
      <div className="mb-2 flex items-center">
        {isTriad ? (
          <IconStack2 size={16} className="shrink-0 text-violet-700" aria-hidden />
        ) : (
          <IconLink size={16} className="shrink-0 text-violet-700" aria-hidden />
        )}
        <h3 className="ml-1.5 text-sm font-bold uppercase tracking-wider text-violet-700">
          Highest Complexity Combination
        </h3>
        <SectionRule color="#c4b5fd" />
      </div>

      <div className={`overflow-hidden rounded-lg border bg-white ${tone.border}`}>
        {/* Header strip */}
        <div
          className={`flex items-start gap-3 border-b px-3 py-3 ${tone.headerBg} ${tone.border}`}
        >
          <div className="flex min-w-0 flex-1 items-start gap-2.5">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white ${tone.accent}`}
            >
              {isTriad ? (
                <IconStack2 size={16} aria-hidden />
              ) : (
                <IconLink size={16} aria-hidden />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wide text-violet-700">
                Clinical Complexity Driver
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="rounded-full bg-violet-700 px-2 py-0.5 text-xs font-bold uppercase text-white">
                  {combination.complexityType}
                </span>
                <span className="text-sm text-gray-600">
                  {diagnosisCount} interacting diagnosis
                  {diagnosisCount === 1 ? "" : "es"}
                </span>
              </div>
            </div>
          </div>

          <div className="min-w-[180px] shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <p className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500">
                Complexity Score
              </p>
              <p
                className={`text-3xl font-bold tabular-nums leading-none ${tone.levelText}`}
              >
                {combination.complexityScore}
              </p>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-slate-200">
              <div
                className={`h-full rounded-full ${tone.accent}`}
                style={{ width: `${score}%` }}
              />
            </div>
          </div>
        </div>

        <div className="px-3 py-3">
          <p className="mb-3 text-base leading-relaxed text-gray-700">
            {combination.clinicalSummary}
          </p>

          <div className="grid gap-0 border-t border-gray-200 md:grid-cols-2">
            <div className="border-gray-200 pt-3 md:border-r md:pr-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Diagnosis Contributions
              </p>
              <div className="mt-2.5 space-y-3">
                {sorted.map((row) => {
                  const bothLong =
                    row.diagnosis.trim().length >= 28 &&
                    row.contribution.trim().length >= 28;

                  return (
                    <div key={`${row.diagnosis}-${row.contribution}`}>
                      <div className="flex items-start gap-3">
                        <p
                          className={`min-w-0 text-sm font-medium leading-snug text-slate-900 ${
                            bothLong ? "w-1/2" : "flex-1"
                          }`}
                        >
                          {row.diagnosis}
                        </p>

                        {row.percent !== null ? (
                          <span
                            className={`min-w-0 break-words text-right text-xs font-bold tabular-nums leading-snug ${
                              bothLong ? "w-1/2" : "max-w-[40%]"
                            } ${tone.levelText}`}
                          >
                            {row.contribution}
                          </span>
                        ) : (
                          <span
                            className={`inline-flex min-w-0 break-words rounded-md px-2 py-1 text-left text-[10px] font-bold uppercase leading-snug ${
                              bothLong ? "w-1/2" : "max-w-[40%]"
                            } ${complexityBadgeClass(row.contribution)}`}
                          >
                            {row.contribution}
                          </span>
                        )}
                      </div>

                      {row.percent !== null ? (
                        <div
                          className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100"
                          aria-hidden
                        >
                          <div
                            className={`h-full rounded-full ${tone.accent}`}
                            style={{ width: `${row.percent}%` }}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-3 md:pl-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                Impact Profile
              </p>
              <div className="divide-y divide-gray-200">
                {(
                  [
                    {
                      icon: IconRefresh,
                      label: "Readmission",
                      value: combination.impactProfile?.readmissionImpact,
                    },
                    {
                      icon: IconHeartbeat,
                      label: "Mortality",
                      value: combination.impactProfile?.mortalityImpact,
                    },
                    {
                      icon: IconActivity,
                      label: "Utilization",
                      value: combination.impactProfile?.utilizationImpact,
                    },
                    {
                      icon: IconArrowsExchange,
                      label: "Disposition",
                      value: combination.impactProfile?.dispositionImpact,
                    },
                  ] as const
                ).map(({ icon: Icon, label, value }) =>
                  value ? (
                    <div
                      key={label}
                      className="flex items-start gap-2 py-2 first:pt-0 last:pb-0"
                    >
                      <Icon
                        size={14}
                        className={`mt-0.5 shrink-0 ${tone.iconColor}`}
                        aria-hidden
                      />
                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                          {label}
                        </p>
                        <p className="text-sm leading-relaxed text-gray-700">
                          {value}
                        </p>
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const RiskComboList: React.FC<{
  title: string;
  kind: "dyad" | "triad";
  items: Array<HighRiskDyad | HighRiskTriad>;
}> = ({ title, kind, items }) => {
  if (!items?.length) return null;

  return (
    <section>
      <div className="mb-2 flex items-center">
        {kind === "triad" ? (
          <IconStack2 size={16} className="shrink-0 text-violet-700" aria-hidden />
        ) : (
          <IconLink size={16} className="shrink-0 text-violet-700" aria-hidden />
        )}
        <h3 className="ml-1.5 text-sm font-bold uppercase tracking-wider text-violet-700">
          {title}
        </h3>
        <SectionRule color="#c4b5fd" />
      </div>

      <div className="space-y-2.5">
        {items.map((item, index) => {
          const tone = getSeverityClasses(item.riskLevel);
          return (
            <div
              key={`${item.conditions.join("+")}-${index}`}
              className={`rounded-lg border p-3 ${tone.bg} ${tone.border}`}
            >
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">
                  {item.conditions.join(" + ")}
                </p>
                <span
                  className={`rounded-md px-2 py-0.5 text-xs font-bold uppercase ${tone.badge}`}
                >
                  {item.riskLevel}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-gray-800">
                {item.clinicalSummary}
              </p>
              {item.impactAreas.length > 0 && (
                <div className="mt-2">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500">
                    Impact Areas
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {item.impactAreas.map((area) => (
                      <span
                        key={area}
                        className="rounded-full border border-violet-200 bg-white px-2 py-0.5 text-xs font-medium text-violet-700"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const BehavioralAmplifierCard: React.FC<{
  amplifier: BehavioralHealthAmplifier;
}> = ({ amplifier }) => (
  <div className="overflow-hidden rounded-lg border border-blue-200 bg-white">
    <div className="flex items-start gap-2.5 border-b border-blue-100 bg-sky-50 px-3 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-white text-blue-600">
        <IconBrain size={16} aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-wide text-blue-700">
          Behavioral Health Amplifier
        </p>
        <p className="text-base font-bold text-gray-900">
          {amplifier.behavioralCondition}
        </p>
      </div>
    </div>

    <div className="px-3 py-3">
      <p className="text-base leading-relaxed text-gray-700">
        {amplifier.amplificationSummary}
      </p>
      {amplifier.amplifiedConditions.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-gray-500">
            Amplified Conditions
          </p>
          <div className="flex flex-wrap gap-1.5">
            {amplifier.amplifiedConditions.map((condition) => (
              <span
                key={condition}
                className="rounded-full border border-blue-200 bg-white px-2.5 py-0.5 text-xs font-medium text-blue-700"
              >
                {condition}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

const ComorbidityAnalysisPanel: React.FC<ComorbidityAnalysisPanelProps> = ({
  data,
}) => {
  if (!data) {
    return (
      <p className="px-1 py-4 text-sm text-gray-500">
        Comorbidity analysis data is not available for this review.
      </p>
    );
  }

  const highest = data.highestComplexityCombination;
  const showFallback = !highest;

  return (
    <div className="space-y-4">
      <ComplexityHeader
        summary={data.summary}
        overallComplexityLevel={data.overallComplexityLevel}
      />

      {highest && <HighestCombinationCard combination={highest} />}

      {showFallback && (
        <>
          <RiskComboList
            title="High Risk Dyads"
            kind="dyad"
            items={data.highRiskDyads ?? []}
          />
          <RiskComboList
            title="High Risk Triads"
            kind="triad"
            items={data.highRiskTriads ?? []}
          />
        </>
      )}

      {(data.behavioralHealthAmplifiers ?? []).length > 0 && (
        <section>
          <div className="mb-2 flex items-center">
            <IconBrain size={16} className="shrink-0 text-blue-700" aria-hidden />
            <h3 className="ml-1.5 text-sm font-bold uppercase tracking-wider text-blue-700">
              Behavioral Health Amplifiers
            </h3>
            <SectionRule />
          </div>
          <div className="space-y-2.5">
            {(data.behavioralHealthAmplifiers ?? []).map((amplifier) => (
              <BehavioralAmplifierCard
                key={amplifier.behavioralCondition}
                amplifier={amplifier}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ComorbidityAnalysisPanel;
